import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useRevalidator } from "react-router-dom";

import useMedicalCompleteLock from "../../../../../../../hooks/useMedicalCompleteLock";
import useRevalidateParent from "../../../../../../../hooks/useRevalidateParent";
import useService from "../../../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../../../global/ApiRequestErrorHandler";
import MedicalCompleteLockNotice from "../../../../../../global/MedicalCompleteLockNotice";
import ModalShell from "../../../../../../global/ModalShell";
import ResponseModal from "../../../../../../global/ResponseModal";
import ProviderDetail from "../../../../../../modules/providers/provider-detail";
import { useProviderSearchStore } from "../../../../../../modules/providers/search-provider/store/providerSearchStore";
import Accordion from "../../../../../../ui library/components/accordions/Accordion";
import AccordionList from "../../../../../../ui library/components/accordions/AccordionList";
import FullyRoundedButton from "../../../../../../ui library/components/buttons/FullyRoundedButton";
import HNSColors from "../../../../../../ui library/HNSColors";
import ProviderHeaderIcon from "../../../../../../ui library/icons/ProviderHeaderIcon";
import AddClientProviderModal from "../AddClientProviderModal";
import Title from "../components/Title";
import DeleteProviderv2 from "../DeleteProviderv2";

import ProviderListDetailsv2 from "./components/CustomProviderAccordion/ProviderListDetailsv2";
import ProviderListSummaryv2 from "./components/CustomProviderAccordion/ProviderListSummaryv2";
import { useProCliStore } from "./hooks/useProCliStore";

export default function ProviderListv2WithState({ caseInfo, providerInfo }) {
  const currentUserService = useService("current-user-service");
  const revalidateParent = useRevalidateParent("rvClientDetail");
  const [proCliResponse, setProCliResponse] = useState(null);
  const [providerData, setProviderData] = useState([]);
  const [openAddProv, setOpenAddProv] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openProvDetail, setOpenProvDetail] = useState(false);
  const [responseModal, setResponseModal] = useState(false);
  const [responseBreakdown, setResponseBreakdown] = useState("");
  const [isError, setIsError] = useState(false);
  const { shouldFetchData, setShouldFetchData, setProvidersData } = useProCliStore();
  const { revalidate } = useRevalidator();
  const [expandedAccordions, setExpandedAccordions] = useState([]);
  const { reset } = useProviderSearchStore();
  const canAddProviderClientStatus = currentUserService?.userRoles?.permissions?.includes(
    "provider.add_providerclientstatus"
  );
  const { isCaseStatusLocked } = useMedicalCompleteLock();
  const caseLocked = isCaseStatusLocked(caseInfo);

  const accordionData = new Map();

  useEffect(() => {
    setProvidersData(createAccordionSummaryData(providerInfo));
  }, [providerInfo]);

  const handleAddProvider = () => {
    reset?.();
    setOpenAddProv(true);
  };

  const handleSuccess = (msg = "") => {
    setResponseModal(true);
    setResponseBreakdown(msg);
    setIsError(false);
    setShouldFetchData(true);
    revalidateParent();
  };

  const handleError = (error) => {
    const errArr = ApiRequestErrorHandler(error.response.data);
    setIsError(true);
    setResponseModal(true);
    setResponseBreakdown(errArr);
  };

  const closeResModal = () => {
    setIsError(false);
    setResponseModal(false);
    setResponseBreakdown("");
    revalidate();
  };

  const getBgSummaryColor = (provider) => {
    if (provider && provider.inNetwork) {
      if (provider.receivingClients) {
        return HNSColors.success[800];
      } else {
        return HNSColors.warning[700];
      }
    }
    return HNSColors.failure[800];
  };

  const createAccordionSummaryData = (data) => {
    data.map((proCliElement, index) => {
      accordionData.set(proCliElement.id, {
        index: proCliElement?.pk || proCliElement?.id,
        bgSummaryColor: getBgSummaryColor(proCliElement?.provider),
        proCliData: proCliElement,
        handleSuccess: handleSuccess,
        handleError: handleError,
        setProviderData: setProviderData,
        setOpenProvDetail: setOpenProvDetail,
        setOpenDeleteModal: setOpenDeleteModal,
        editableFields: null,
      });
    });
    setProCliResponse(accordionData);
    return accordionData;
  };

  return (
    <div className="relative h-full overflow-y-auto">
      <div className="h-full overflow-y-auto p-1">
        <div className="px-8">
          <Title text={"Providers"} />
        </div>

        <div data-test-id="provider-page-introduction" className="flex flex-row items-center p-4">
          <div className="px-4">
            <ProviderHeaderIcon />
          </div>
          <div className="flex flex-1 flex-row items-end">
            <div className="flex flex-col pr-8">
              <div id="provider-page-info-header">
                <Title text={"Provider Client Relationship"} />
              </div>
              <div id="provider-page-info">
                <h1 className="text-sm font-medium">
                  A strong Provider-Client Relationship thrives on trust, communication, and
                  efficiency fostering long-term partnerships that improve patient outcomes and
                  business success.
                </h1>
              </div>
            </div>
            {canAddProviderClientStatus ? (
              <div>
                <FullyRoundedButton
                  buttonText="+&nbsp;Add&nbsp;Provider"
                  disabled={caseLocked}
                  handleOnClick={handleAddProvider}
                />
              </div>
            ) : null}
          </div>
        </div>

        <MedicalCompleteLockNotice locked={caseLocked} className="mx-8 mb-2" />

        <div className="w-full px-5 py-4">
          {shouldFetchData ? (
            <div className="flex h-full w-full items-center justify-center p-10">
              <CircularProgress color="secondary" />
            </div>
          ) : proCliResponse && proCliResponse.size === 0 ? (
            <div className="w-full">
              <p className="my-2 text-center text-lg font-bold text-black">No providers found</p>
            </div>
          ) : (
            <>
              <AccordionList
                accordionListData={proCliResponse}
                accordionVariant="default"
                summaryContent={ProviderListSummaryv2}
                expandedContent={<ProviderListDetailsv2 />}
                expandIcon={<ExpandMore className="text-blue-500" />}
                shrinkIcon={<ExpandLess className="text-blue-500" />}
                totalExpandedAccordions={{
                  expandedAccordions,
                  setExpandedAccordions,
                }}
              >
                <Accordion />
              </AccordionList>
            </>
          )}
        </div>

        {openProvDetail ? (
          <ProviderDetail
            providerDetail={providerData}
            open={openProvDetail}
            setOpen={setOpenProvDetail}
          />
        ) : (
          ""
        )}

        {openDeleteModal ? (
          <ModalShell
            open={openDeleteModal}
            setOpen={setOpenDeleteModal}
            title={"Delete Provider"}
            color={"#d32f2f"}
            customWidth={"md"}
          >
            <DeleteProviderv2
              providerDetail={providerData}
              setDeleteFormDisplay={setOpenDeleteModal}
              caseInfo={caseInfo}
            />
          </ModalShell>
        ) : (
          ""
        )}

        <AddClientProviderModal open={openAddProv} setOpen={setOpenAddProv} caseInfo={caseInfo} />

        {!responseModal ? (
          ""
        ) : (
          <ResponseModal
            title={`${isError ? "Failed" : "Successful"}`}
            isError={isError}
            description={isError ? "" : responseBreakdown}
            openBool={responseModal}
            setOpenBool={setResponseModal}
            errorMessage={responseBreakdown}
            handleCloseFunc={closeResModal}
          />
        )}
      </div>
    </div>
  );
}
